import * as React from "react";
import {cx}       from "emotion";

import {cs}       from "@Common/react/chain-services";
import {UseState} from "@Common/react/use-state";
import {Input}    from "./input";
import {UseRef}   from "@Common/react/use-ref";


export const PhoneFormat = ({
    specField,
    val,
    smartPhoneList,
    branchIsInternational,
    error,
    countable,
    readonlyVal,
}:{
    specField             : any,
    val                   : string
    smartPhoneList        : any,
    branchIsInternational : boolean,
    error                 : boolean,
    countable             : string,
    readonlyVal           : boolean,
}) => cs(

    ["state", (_, next) => UseState({
        _key: val,
        initValue: {
            content     : val,
            // If the phone format is set to "smartphone" extract the country code from the input value.
            // Example: For a value like "+86 755 8433 9000", the country code should be extracted as "+86".
            countryCode : val?.split(' ')[0] == "+1" ? val?.split(" ")[0] : branchIsInternational ? (specField.idTemplateFieldFormat?.split(",").includes("27") ? val?.split(" ")[0] : '+') : '+1',
            branchIsInternational : branchIsInternational,
            initialLoad: true
        },
        next
    })],

    ["inputRef", (_, next) => UseRef({next})],

    ["formatPhoneNumber", ({state}, next) => next((fieldFormat, e) => {
        let curval = e?.target.value;
        let mask;
        let imask = require("imask");

        switch(fieldFormat) {
            case "phone_1":
                mask = '000.000.0000';
                break;
            case "phone_2":
                mask = '000-000-0000';
                break;
            case "phone_3":
                mask = '000 000 0000';
                break;
            case "phone_4":
                mask = '(000) 000.0000';
                break;
            case "phone_5":
                mask = '(000) 000-0000';
                break;
            case "phone_6":
                mask = '(000) 000 0000';
                break;
            case "phone_7":
                mask = '+1 000.000.0000';
                break;
            case "phone_8":
                mask = '+1 000-000-0000';
                break;
            case "phone_9":
                mask = '+1 000 000 0000';
                break;
            case "phone_10":
                mask = '+1 (000) 000.0000';
                break;
            case "phone_11":
                mask = '+1 (000) 000-0000';
                break;
            case "phone_12":
                mask = '+1 (000) 000 0000';
                break;
            case "phone_13":
                mask = '000 000-0000';
                break;
            case "phone_14":
                mask = '+1 000 000-0000';
                break;
        }

        //Smart Phone and Domestic vs International
        if(specField.idTemplateFieldFormat?.split(",").includes("27")
            || specField.idTemplateFieldFormat?.split(",").includes("28")){
            //if the selected country code aligns with the mask, apply it
            if(state.value.countryCode == "+1" && mask) {
                let maskObj = imask.createMask({
                    mask: mask,
                });
                curval = maskObj.resolve(curval ? curval : '+1') ?? maskObj.value;
            //otherwise just apply the country code and remove non numeric characters
            } else {
                let i, j;
                for(i=0, j=0; i<state.value.countryCode.length && j<curval.length; j++) {
                    if(state.value.countryCode[i] == curval[j]){
                        i++;
                    }
                }
                curval = curval.substring(i);
                if(specField.idTemplateFieldFormat?.split(",").includes("28")) {
                    curval = curval.replace(/^\s+/, '');
                }
                const maskPattern = state.value.countryCode + " " + (specField.idTemplateFieldFormat?.split(",").includes("28")
                    ? curval.replace(/[^ ]/g, '0') : "0".repeat(curval.length));
                let maskObj = imask.createMask({
                    mask: maskPattern
                });
                curval = maskObj.resolve(curval ? curval : state.value.countryCode) ?? maskObj.value;
            }
        } else if(mask) {
            let maskObj = imask.createMask({
                mask: mask,
            });
            curval = maskObj.resolve(curval ? curval : '') ?? maskObj.value;
        }
        return curval;
    })],

    ["setInternational", ({state}, next) => next(() => {
        if(branchIsInternational !== state.value.branchIsInternational) {
            state.onChange({...state.value, content: '', countryCode: (branchIsInternational ? '+' : '+1'), branchIsInternational: branchIsInternational})
        }
    })],

    ["setCursorPos", ({state, inputRef}, next) => next(() => {
        var ref = inputRef.get();
        //If the input field is not yet available, abort.
        //If a format has not been applied that changes the input field, then we don't need to move the cursor.
        if(ref == null || !state.value.beforeFormat || state.value.content == state.value.beforeFormat) {
            return;
        }

        //If there has been a new format applied, then the text field will update and automatically move our cursor to the end.
        //We need to invoke another render where the field doesn't change so we can place the cursor manually.
        //If newPos hasn't been set yet, then we haven't triggered a rerender after the text change yet. We'll find the new position here.
        if(state.value.newPos == undefined && state.value.beforeFormat !== ""){
            //If the cursor was at the end of the string anyways then we're happy with the cursor getting pushed to the end.
            //We don't need to do anything.
            if(state.value.beforeFormat.length == ref.selectionStart) {
                return;
            }
            //Otherwise, we need to place the cursor immediately after the newly typed character.
            //We can tell which one it is because it's the one right before the cursor before formatting.
            //So we'll take the substring up to the current cursor in the string before formatting,
            const sub = state.value.beforeFormat.substring(0, ref.selectionStart);
            //then find where that string ends in the string after formatting, accounting for new characters inserted.
            let i, j;
            for(i=0, j=0; i<sub.length && j<state.value.content.length; j++) {
                if(state.value.beforeFormat[i] == state.value.content[j]){
                    i++;
                }
            }
            //Now we'll trigger a new render with this new info about where the cursor should be.
            state.onChange({...state.value, newPos: j});
            return;
        }
        //If we're here then a new position was set on the last render, so apply it.
        ref.selectionStart = state.value.newPos;
        ref.selectionEnd = state.value.newPos;
        //Reset beforeFormat and newPos so we know we're done placing the cursor and don't end up here again.
        state.onChange({...state.value, beforeFormat: undefined, newPos: undefined});
    })],

    ({state, inputRef, formatPhoneNumber, setInternational, setCursorPos}) => (
        <>
            {
                state.value.initialLoad ? state.onChange({
                    ...state.value,
                    content: state.value.content ? formatPhoneNumber(specField?.fieldFormatName, {target: {value: state.value.content}, keyCode: 0}) : "",
                    initialLoad: false,
                }) : ""
            }
            { setInternational() }
            { specField.idTemplateFieldFormat?.split(",").includes("28") &&
                <select name={specField.fieldKey + '-1'} data-fieldname={specField.displayLabel}
                xd-qa-type= 'dropdown'
                xd-qa-name= {specField.displayLabel}
                disabled={readonlyVal}
                    onChange={(e) => {
                        state.onChange({
                            ...state.value,
                            content: "",
                            countryCode: e.target.value
                        });
                    }}>
                    <option value='+1'xd-qa-type="dropdown_item" xd-qa-name="US and Canada">US and Canada</option>
                    <option value='+'xd-qa-type="dropdown_item" xd-qa-name="International" selected={ (state.value.content.split(' ')[0] == "+1" ? state.value.content.split(" ")[0] : state.value.branchIsInternational ? '+' : '+1') == '+'}>International</option>
                </select>
            }
            { specField.idTemplateFieldFormat?.split(",").includes("27") &&
                <select className="smart-phone-select-123" name={specField.fieldKey + '-1'} data-fieldname={specField.displayLabel}
                    xd-qa-type= 'dropdown'
                    xd-qa-name= {specField.displayLabel}
                    disabled={readonlyVal}
                    onChange={(e) => {
                        state.onChange({
                            ...state.value,
                            content: (state.value.content) ? e.target.value.split(";")[0]+" "+(state.value.content.split(" ")[1] ? state.value.content.split(" ")[1] : '' ) : '',
                            countryCode: e.target.value.split(";")[0],
                            selectedCountry: e.target.value.split(";")[1]
                        });
                    }}>
                        {
                            smartPhoneList?.map(list => (
                                <option key={list.dial_code}
                                    value={list.dial_code + ";" + list.code}
                                    xd-qa-type="dropdown_item"
                                    xd-qa-name={list.dial_code}
                                    selected={ state.value.selectedCountry ? state.value.selectedCountry == list.code : (state.value.content ? state.value.content.split(" ")[0] : state.value.countryCode) == list.dial_code} >
                                    {list.name}
                                </option>
                            ))
                        }
                </select>
            }
            <Input
                initRef={inputRef}
                inputField={specField}
                fieldValue={state.value.content}
                className={cx(specField.idDisplayStyle == 2 && 'editable-spec-field',{countable, error: (error && state.value.content == "" && specField.required)})}
                changeHandler={(e) => {
                    (!(specField.idTemplateFieldFormat?.split(",").includes("28") && e.target.selectionStart < 2 && !(e.keyCode != undefined)) || (e?.target.value.length == 0 && e.target.selectionStart == 0 ))
                        ? state.onChange({
                            ...state.value,
                            beforeFormat: e?.target.value,
                            content: formatPhoneNumber(specField?.fieldFormatName, e),
                        }) : {};
                }}
                readOnly={readonlyVal}
                extraInfo={specField.idDisplayStyle == 2 && {"data-multipartfield": "data" }}
            />
            { setCursorPos() }
        </>
    )

);
